import { useLocation } from "react-router-dom";
import { useAppState } from "../../../AppContext";
import { StyledMenuItem } from "../../Sidebar/MenuItemStyles";
import ConfirmDialog from "../BaseComponents/ConfirmDialog";
import SpinnerWrapper from "../BaseComponents/SpinnerWrapper";
import { FormattedMessage } from "react-intl";
import { Button } from "../EditorStyles";
import { useCallback, useEffect, useState } from "react";
import { useEditState } from "../EditContext";
import { useRestAuth } from "../../../hooks/useRestAuth";
import { ReactComponent as PublishIcon } from "./../../../images/editor-icons/rocket.svg";
import { ReactComponent as DiscardIcon } from "./../../../images/editor-icons/trash.svg";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { useClearMapCacheIfNeeded } from "../Map/useMapDataOptional";

const PanoPage = () => {
  const { refresh } = useAppState();
  const location = useLocation();
  const [publishChanges, setPublishChanges] = useState(false);
  const [discardChanges, setDiscardChanges] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const {
    panoLinkRepository,
    panoPoiRepository,
    mapPanoRepository,
    mapPoiRepository,
    changesRepository,
    target,
    editData,
  } = useEditState();
  const restAuth = useRestAuth();
  const [changes, setChanges] = useState<string[]>([]);
  const clearMapCacheIfNeeded = useClearMapCacheIfNeeded();

  const fetchChanges = useCallback(async () => {
    if (restAuth) {
      setIsLoading(true);
      try {
        const fetched = await changesRepository.get(restAuth);
        setChanges(fetched);
      } finally {
        setIsLoading(false);
      }
    }
  }, [restAuth, changesRepository]);

  useEffect(() => {
    fetchChanges();
  }, [fetchChanges]);

  const refreshAllData = useCallback(async () => {
    if (restAuth) {
      if (location.pathname.startsWith("/editor/pano") && target) {
        const [panoramaLocation, poiLocation] = await Promise.all([
          panoLinkRepository.get(restAuth, target),
          panoPoiRepository.get(restAuth, target),
        ]);

        editData<"pano">({
          panorama_location: panoramaLocation,
          poi_location: poiLocation,
        });
      } else if (location.pathname.startsWith("/editor/map")) {
        if (target) {
          const [panoramaLocation, poiLocation, unlinkedPanoramaLocation, unlinkedPoiLocation] =
            await Promise.all([
              mapPanoRepository.get(restAuth, target),
              mapPoiRepository.get(restAuth, target),
              mapPanoRepository.get_unlinked(restAuth, target),
              mapPoiRepository.get_unlinked(restAuth, target),
            ]);

          editData<"map">({
            panorama_location: panoramaLocation,
            poi_location: poiLocation,
            unlinked_panorama_location: unlinkedPanoramaLocation,
            unlinked_poi_location: unlinkedPoiLocation,
          });
        } else {
          const [panoramaLocation, poiLocation, unusedPanoramaLocation, unusedPoiLocation] =
            await Promise.all([
              mapPanoRepository.get(restAuth),
              mapPoiRepository.get(restAuth, target),
              mapPanoRepository.get_unused(restAuth),
              mapPoiRepository.get_unused(restAuth),
            ]);

          editData<"map">({
            panorama_location: panoramaLocation,
            poi_location: poiLocation,
            unused_panorama_location: unusedPanoramaLocation,
            unused_poi_location: unusedPoiLocation,
          });
        }
      }
    }
  }, [
    restAuth,
    location.pathname,
    target,
    editData,
    panoLinkRepository,
    panoPoiRepository,
    mapPanoRepository,
    mapPoiRepository,
  ]);

  const onPublishChanges = useCallback(async () => {
    await changesRepository.publish(restAuth);
    refresh();
    clearMapCacheIfNeeded(); // Leeg de map cache als we in map editor zijn
    setPublishChanges(false);
    await fetchChanges();
  }, [changesRepository, restAuth, refresh, fetchChanges, clearMapCacheIfNeeded]);

  const onDiscardChanges = useCallback(async () => {
    await changesRepository.discard(restAuth);
    await refreshAllData();
    clearMapCacheIfNeeded(); // Leeg de map cache als we in map editor zijn
    setDiscardChanges(false);
    await fetchChanges();
  }, [changesRepository, restAuth, refreshAllData, fetchChanges, clearMapCacheIfNeeded]);

  return (
    <SpinnerWrapper loading={isLoading}>
      <div style={{ overflowY: "auto" }}>
        <div style={{ margin: "16px", display: "flex", justifyContent: "center", gap: "8px" }}>
          <Button 
            onClick={() => setDiscardChanges(true)} 
            className="danger"
          >
            <DiscardIcon />
            <FormattedMessage
              id="editor.publish.discard-button"
              defaultMessage="Discard all changes"
              description="Button text to discard all changes"
            />
          </Button>
          <Button 
            onClick={() => setPublishChanges(true)} 
            className="success"
          >
            <PublishIcon />
            <FormattedMessage
              id="editor.publish.publish-button"
              defaultMessage="Publish all changes"
              description="Button text to publish all changes"
            />
          </Button>
        </div>

      {changes.length === 0 ? (
        <div style={{ margin: "16px" }}>
          <FormattedMessage
            id="editor.changes.no-changes"
            defaultMessage="You have not made any changes yet."
            description="No changes message"
          />
        </div>
      ) : (
        <div>
          {changes.map((change, index) => (
            <StyledMenuItem key={index}>
              <ReactMarkdown
                rehypePlugins={[rehypeRaw]}
                remarkPlugins={[remarkGfm]}
                children={change}
                components={{
                  table: ({ children }) => (
                    <table
                      style={{
                        width: "100%",
                        backgroundColor: "#f5f5f5",
                        borderRadius: 8,
                        padding: "4px",
                      }}
                    >
                      {children}
                    </table>
                  ),
                  th: ({ children }) => (
                    <th style={{ textAlign: "left", padding: "4px" }}>{children}</th>
                  ),
                  td: ({ children }) => <td style={{ padding: "4px" }}>{children}</td>,
                }}
              />
            </StyledMenuItem>
          ))}
        </div>
      )}

      <ConfirmDialog 
        show={discardChanges} 
        setShow={setDiscardChanges} 
        onConfirm={onDiscardChanges}
      >
        <h2>
          <FormattedMessage
            id="editor.publish.discard-popup-header"
            defaultMessage="Are you sure you want to discard the changes you made?"
            description="Header text form discard all changes popup dialog"
          />
        </h2>
        <p>
          <FormattedMessage
            id="editor.publish.discard-popup-body"
            defaultMessage="This action cannot be undone."
            description="Body text form discard all changes popup dialog"
          />
        </p>
      </ConfirmDialog>
      <ConfirmDialog 
        show={publishChanges} 
        setShow={setPublishChanges} 
        onConfirm={onPublishChanges}
      >
        <h2>
          <FormattedMessage
            id="editor.publish.publish-popup-header"
            defaultMessage="Are you sure you want to publish the changes you made?"
            description="Header text form publish all changes popup dialog"
          />
        </h2>
        <p>
          <FormattedMessage
            id="editor.publish.publish-popup-body"
            defaultMessage="This action cannot be undone."
            description="Body text form publish all changes popup dialog"
          />
        </p>
      </ConfirmDialog>
    </div>
    </SpinnerWrapper>
  );
};

export default PanoPage;
